import { useCallback, useRef, useEffect, useState } from 'react'

const PULL_BOTTOM_THRESHOLD = 50
const FIRST_MESSAGE_TOP_THRESHOLD = 24

export const useChatScroll = (blocksCount: number) => {
  const feedBodyRef = useRef<HTMLDivElement>(undefined)
  const wasAtBottomRef = useRef(true)
  const machineTriggeredScroll = useRef(false)
  const [isScrolledBeyondFirstMessage, setIsScrolledBeyondFirstMessage] = useState(false)

  const updateIsScrolledBeyondFirstMessage = useCallback((scrollContainer: Element) => {
    const distanceToTop = scrollContainer.scrollTop ?? 0
    if (distanceToTop > FIRST_MESSAGE_TOP_THRESHOLD) {
      setIsScrolledBeyondFirstMessage(true)
    } else {
      setIsScrolledBeyondFirstMessage(false)
    }
  }, [])

  const updateWasAtBottom = useCallback((scrollContainer: Element) => {
    if (machineTriggeredScroll.current) {
      machineTriggeredScroll.current = false
      return
    }

    const scrollHeight = scrollContainer.scrollHeight ?? 0
    const divHeight = scrollContainer.clientHeight ?? 0
    const distanceToTop = scrollContainer.scrollTop ?? 0

    wasAtBottomRef.current = scrollHeight - (divHeight + distanceToTop) < PULL_BOTTOM_THRESHOLD
  }, [])

  const scrollDown = useCallback((container: Element) => {
    const scrollContainer = container.parentElement
    if (scrollContainer) {
      const scrollHeightAfterPaint = container.scrollHeight ?? 0
      const divHeightAfterPaint = scrollContainer.clientHeight ?? 0
      const distanceToTopAfterPaint = scrollContainer.scrollTop ?? 0

      const scrollIncreasedValue =
        scrollHeightAfterPaint - (divHeightAfterPaint + distanceToTopAfterPaint)

      machineTriggeredScroll.current = true
      scrollContainer.scrollTop = scrollContainer.scrollTop + scrollIncreasedValue
    }
  }, [])

  const observer = useRef(
    new ResizeObserver(entries => {
      if (wasAtBottomRef.current && entries[0]) {
        scrollDown(entries[0].target)
      }
    })
  )

  const setRef = useCallback(
    (container: HTMLDivElement | null) => {
      if (container) {
        feedBodyRef.current = container
        observer.current.observe(container)

        if (container.parentElement) {
          container.parentElement.onscroll = e => {
            const scrollContainer = e.target as Element
            updateIsScrolledBeyondFirstMessage(scrollContainer)
            updateWasAtBottom(scrollContainer)
          }
        }
      }
    },
    [updateIsScrolledBeyondFirstMessage, updateWasAtBottom]
  )
  useEffect(() => () => observer.current.disconnect(), [])

  useEffect(() => {
    const container = feedBodyRef.current
    if (container && wasAtBottomRef.current) {
      scrollDown(container)
    }
  }, [blocksCount, scrollDown])

  return { ref: setRef, isScrolledBeyondFirstMessage }
}
